import { useId } from "react";
import { Modal, Icon } from "@legal-500/legal500-design-system";
import styles from "./Modal.module.css";

/** One row as the user saw it when they ran the bulk action; describes the affected items. */
export type BulkResultRow = {
  id: string;
  firmName: string | null;
  firmRef: string | null;
  lawyerName: string | null;
  contentOutput: string | null;
};

export type BulkResultAction = "approve" | "reject" | "suspend";

type BulkResultModalProps = {
  isOpen: boolean;
  action: BulkResultAction;
  updatedCount: number;
  lockedIds: string[];
  notFoundIds: string[];
  invalidStatusIds: string[];
  alreadyProcessedIds: string[];
  callbackFailedIds: string[];
  /** The rows the user had on screen when they clicked; used to describe affected items. */
  rows: BulkResultRow[];
  onClose: () => void;
};

/** A non-empty group of ids that were not changed, with the sentence that explains why. */
type IssueGroup = { key: string; ids: string[]; message: string };

const PAST_TENSE: Record<BulkResultAction, string> = {
  approve: "approved",
  reject: "rejected",
  suspend: "suspended",
};
const PAST_TENSE_CAPITALIZED: Record<BulkResultAction, string> = {
  approve: "Approved",
  reject: "Rejected",
  suspend: "Suspended",
};
const CONTENT_PREVIEW_CHARS = 80;

/** `n` followed by the singular or the plural phrase: "1 request was", "2 requests were". */
function counted(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

function alreadyProcessedMessage(n: number, action: BulkResultAction): string {
  const skipped = action === "approve" ? "skipped and nothing was re-sent" : "skipped";
  return (
    `${counted(n, "request was", "requests were")} already ${PAST_TENSE[action]} by someone ` +
    `else after you loaded this list. ${n === 1 ? "It was" : "They were"} ${skipped}.`
  );
}

function invalidStatusMessage(n: number, action: BulkResultAction): string {
  const single = n === 1;
  return (
    `${counted(n, "request", "requests")} could not be ${PAST_TENSE[action]} because of ` +
    `${single ? "its" : "their"} current status. Refresh the list to see ` +
    `${single ? "it" : "them"}.`
  );
}

function lockedMessage(n: number): string {
  return (
    `${counted(n, "request was", "requests were")} skipped because ` +
    `${n === 1 ? "it is" : "they are"} locked by another user`
  );
}

function callbackFailedMessage(n: number, action: BulkResultAction): string {
  return (
    `${counted(n, "request", "requests")} could not be ${PAST_TENSE[action]} because the ` +
    "content pipeline did not confirm the change. " +
    `${n === 1 ? "It keeps its" : "They keep their"} previous status; try again in a moment.`
  );
}

function notFoundMessage(n: number): string {
  return `${counted(n, "request was", "requests were")} not found`;
}

function contentPreview(content: string): string {
  return content.length > CONTENT_PREVIEW_CHARS
    ? `${content.slice(0, CONTENT_PREVIEW_CHARS).trimEnd()}…`
    : content;
}

/** "Firm (ref) · Lawyer · first 80 characters of the content…"; the bare id when not on screen. */
export function describeRow(id: string, row: BulkResultRow | undefined): string {
  if (!row) return id;
  const firmRef = row.firmRef?.trim();
  const firm = [row.firmName?.trim(), firmRef && `(${firmRef})`].filter(Boolean).join(" ");
  const content = row.contentOutput?.trim();
  const description = [firm, row.lawyerName?.trim(), content && contentPreview(content)]
    .filter(Boolean)
    .join(" · ");
  return description || id;
}

export function BulkResultModal({
  isOpen,
  action,
  updatedCount,
  lockedIds,
  notFoundIds,
  invalidStatusIds,
  alreadyProcessedIds,
  callbackFailedIds,
  rows,
  onClose,
}: BulkResultModalProps) {
  const idPrefix = useId();
  if (!isOpen) return null;

  const rowsById = new Map(rows.map(row => [row.id, row] as const));
  // Shown in this order, one block per non-empty group.
  const issues: IssueGroup[] = [
    {
      key: "already-processed",
      ids: alreadyProcessedIds,
      message: alreadyProcessedMessage(alreadyProcessedIds.length, action),
    },
    {
      key: "invalid-status",
      ids: invalidStatusIds,
      message: invalidStatusMessage(invalidStatusIds.length, action),
    },
    { key: "locked", ids: lockedIds, message: lockedMessage(lockedIds.length) },
    {
      key: "callback-failed",
      ids: callbackFailedIds,
      message: callbackFailedMessage(callbackFailedIds.length, action),
    },
    { key: "not-found", ids: notFoundIds, message: notFoundMessage(notFoundIds.length) },
  ].filter(issue => issue.ids.length > 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Bulk ${PAST_TENSE_CAPITALIZED[action]}`}
      size="medium"
      blurBackground
    >
      <div className={styles.deleteModal}>
        {updatedCount > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <div
              className={styles.deleteMessage}
              style={{
                color: "var(--color-success)",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Icon icon="check" size="small" />
              <span>
                {updatedCount} {updatedCount === 1 ? "request" : "requests"} {PAST_TENSE[action]}{" "}
                successfully
              </span>
            </div>
          </div>
        )}

        {issues.length > 0 && (
          <div>
            {issues.map(issue => {
              const labelId = `${idPrefix}-${issue.key}`;
              return (
                <div key={issue.key} style={{ marginBottom: "16px" }}>
                  <div
                    className={styles.deleteWarning}
                    style={{ display: "flex", alignItems: "center", gap: "8px" }}
                  >
                    <Icon icon="exclamation" size="small" />
                    <span id={labelId}>{issue.message}</span>
                  </div>
                  <ul className={styles.bulkResultList} aria-labelledby={labelId}>
                    {issue.ids.map(id => (
                      <li key={id}>{describeRow(id, rowsById.get(id))}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        {updatedCount === 0 && issues.length === 0 && (
          <p className={styles.deleteMessage}>No requests were processed.</p>
        )}
      </div>
    </Modal>
  );
}
